import { useState } from 'react';
import { Button, Label } from '@lognai/synapse-design-system-v2';
import { usePrimaryColor, normalizeHex } from '../hooks/usePrimaryColor';

// The brand navy. Must match `primary` in the design system's light theme, or
// "Reset to default" would hand back a color the theme never actually uses.
const DEFAULT_COLOR = '#012d69';
const PRESETS = ['#012d69', '#e40521', '#7c3aed', '#db2777', '#dc2626', '#ea580c', '#16a34a', '#0891b2'];

/**
 * Inline primary-color picker for the Settings → Appearance section.
 * Changes preview instantly and persist via the usePrimaryColor hook.
 */
export function PrimaryColorPicker() {
  const { color, setPrimaryColor } = usePrimaryColor();
  const [value, setValue] = useState(color ?? DEFAULT_COLOR);

  const normalized = normalizeHex(value);

  // Live preview as the user picks a color.
  const preview = (next: string) => {
    setValue(next);
    const hex = normalizeHex(next);
    if (hex) setPrimaryColor(hex);
  };

  const handleReset = () => {
    setPrimaryColor(null);
    setValue(DEFAULT_COLOR);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="primary-color-picker">Primary color</Label>
        <input
          id="primary-color-picker"
          type="color"
          value={normalized ?? DEFAULT_COLOR}
          onChange={(e) => preview(e.target.value)}
          className="mt-1 h-10 w-14 cursor-pointer rounded-md border border-border bg-surface p-1"
        />
      </div>

      <div>
        <Label>Presets</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {PRESETS.map((hex) => (
            <button
              key={hex}
              type="button"
              aria-label={hex}
              onClick={() => preview(hex)}
              className="h-7 w-7 rounded-full border border-border ring-offset-2 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ backgroundColor: hex }}
            />
          ))}
        </div>
      </div>

      <Button variant="outline" size="sm" onClick={handleReset}>
        Reset to default
      </Button>
    </div>
  );
}
